import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronLeft, ChevronRight, Download, Filter, Search, Star, Users } from 'lucide-react';
import { TYPE_CONGE_LABELS } from '../lib/conges';

export const MONTH_NAMES_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const WEEKDAY_LETTERS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

const GRID_TYPE_LABELS = {
  cp: 'Congés payés',
  rtt: 'RTT',
  maladie: 'Maladie',
  sans_solde: 'Sans solde',
  recuperation: 'Récupération',
  exceptionnel: 'Exceptionnel',
  formation: 'Formation'
};

const formatFr = (dateStr) => {
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR');
  } catch {
    return dateStr;
  }
};

/**
 * Grille "planning mensuel" : une ligne par collaborateur, une colonne par jour du mois.
 *
 * Props :
 *  - year, month (1-12)
 *  - data : réponse de GET /calendar/absences ({ events, employees, departments, type_colors, journee_solidarite })
 *  - joursFeries : réponse de GET /calendar/jours-feries
 *  - loading : affiche un spinner
 *  - selectedDepartment / onDepartmentChange : filtre département (piloté par le parent, déclenche le refetch)
 *  - onNavigate(direction) / onToday : navigation mensuelle
 *  - onExport / exporting : bouton "Exporter Excel" (facultatif)
 *  - showToolbar : affiche la barre navigation + département (true par défaut) ;
 *    la recherche par nom et la légende sont toujours affichées.
 *  - showLegend : affiche la légende (true par défaut)
 */
const TeamAbsencesGrid = ({
  year,
  month,
  data,
  joursFeries = [],
  loading = false,
  selectedDepartment = 'all',
  onDepartmentChange,
  onNavigate,
  onToday,
  onExport,
  exporting = false,
  showToolbar = true,
  showLegend = true,
  title
}) => {
  const [search, setSearch] = useState('');

  const events = useMemo(() => data?.events || [], [data]);
  const employees = useMemo(() => data?.employees || [], [data]);
  const departments = data?.departments || [];
  const typeColors = data?.type_colors || {};
  const journeeSolidarite = data?.journee_solidarite || null;

  // Jours du mois : calculés une seule fois par mois affiché
  const monthDays = useMemo(() => {
    const days = [];
    const daysInMonth = new Date(year, month, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const date = new Date(year, month - 1, d);
      const dow = date.getDay();
      days.push({ day: d, dateStr, date, dow, isWeekend: dow === 0 || dow === 6 });
    }
    return days;
  }, [year, month]);

  const todayStr = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  }, []);

  const jfMap = useMemo(() => {
    const map = {};
    (joursFeries || []).forEach((jf) => { if (jf?.date) map[jf.date] = jf; });
    return map;
  }, [joursFeries]);

  const isJourneeSolidarite = (dateStr) =>
    Boolean(journeeSolidarite?.travaillee && journeeSolidarite.date === dateStr);

  // La journée de solidarité travaillée n'est pas un jour chômé
  const getJourFerie = (dateStr) => (isJourneeSolidarite(dateStr) ? null : jfMap[dateStr]);

  // Événements indexés par collaborateur
  const eventsByEmployee = useMemo(() => {
    const map = {};
    events.forEach((ev) => {
      if (!map[ev.employee_id]) map[ev.employee_id] = [];
      map[ev.employee_id].push(ev);
    });
    return map;
  }, [events]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((emp) =>
      `${emp.name || ''} ${emp.prenom || ''} ${emp.nom || ''} ${emp.department || ''}`.toLowerCase().includes(q)
    );
  }, [employees, search]);

  const typeLabel = (type) => GRID_TYPE_LABELS[type] || TYPE_CONGE_LABELS[type] || type;

  const eventTooltip = (emp, ev) =>
    `${emp.name} - ${typeLabel(ev.type)} (${formatFr(ev.start)} au ${formatFr(ev.end)}${ev.is_approved ? ', validé' : ', en attente'})`;

  const renderCell = (emp, d) => {
    const jf = getJourFerie(d.dateStr);
    const solidarite = isJourneeSolidarite(d.dateStr);
    const dayEvents = (eventsByEmployee[emp.id] || []).filter((e) => d.dateStr >= e.start && d.dateStr <= e.end);
    const isChome = Boolean(jf) || d.isWeekend;

    let cellClass = 'border border-slate-200 dark:border-slate-700 p-0 h-8 min-w-[26px] text-center align-middle';
    if (dayEvents.length === 0) {
      if (isChome) cellClass += ' bg-slate-100 dark:bg-slate-800';
      else if (solidarite) cellClass += ' bg-purple-50 dark:bg-purple-900/20';
      else if (d.dateStr === todayStr) cellClass += ' bg-[#0F5156]/5';
    }

    return (
      <td
        key={d.dateStr}
        className={cellClass}
        title={dayEvents.length ? dayEvents.map((ev) => eventTooltip(emp, ev)).join('\n') : (jf ? jf.nom : solidarite ? 'Journée de solidarité (travaillée)' : '')}
      >
        {dayEvents.length > 0 && (
          <div className="w-full h-8 flex flex-col gap-px">
            {dayEvents.map((ev) => {
              const demiDebut = ev.demi_jour_debut && d.dateStr === ev.start;
              const demiFin = ev.demi_jour_fin && d.dateStr === ev.end;
              // Début l'après-midi = moitié droite ; fin le midi = moitié gauche
              const style = {
                backgroundColor: ev.color,
                opacity: ev.is_approved ? 1 : 0.55,
                width: (demiDebut || demiFin) ? '50%' : '100%',
                marginLeft: demiDebut && !demiFin ? 'auto' : undefined
              };
              return (
                <div key={ev.id} className="flex-1 min-h-[3px] flex">
                  <div
                    className="h-full flex items-center justify-center text-[9px] font-bold text-white leading-none"
                    style={style}
                  >
                    {(demiDebut || demiFin) ? '½' : ''}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </td>
    );
  };

  const toolbar = (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        {showToolbar && (
          <>
            <Button variant="outline" size="icon" onClick={() => onNavigate && onNavigate(-1)} aria-label="Mois précédent">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={() => onNavigate && onNavigate(1)} aria-label="Mois suivant">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => onToday && onToday()}>
              Aujourd'hui
            </Button>
            <CardTitle className="text-lg ml-2 whitespace-nowrap">
              {title || `${MONTH_NAMES_FR[month - 1]} ${year}`}
            </CardTitle>
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher un collaborateur..."
            className="pl-9 w-[220px]"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="planning-search-input"
          />
        </div>
        {showToolbar && onDepartmentChange && (
          <Select value={selectedDepartment} onValueChange={onDepartmentChange}>
            <SelectTrigger className="w-[210px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Tous les départements" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les départements</SelectItem>
              {departments.map((dept) => (
                <SelectItem key={dept.code || dept} value={dept.code || dept}>
                  <div className="flex items-center gap-2">
                    {dept.color && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: dept.color }} />}
                    {dept.label || dept}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {onExport && (
          <Button variant="outline" size="sm" onClick={onExport} disabled={exporting || loading} data-testid="planning-export-btn">
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Export en cours…' : 'Exporter Excel'}
          </Button>
        )}
        <div className="flex items-center gap-1 text-sm text-slate-500 whitespace-nowrap">
          <Users className="w-4 h-4" />
          {events.length} absence(s)
        </div>
      </div>
    </div>
  );

  return (
    <Card data-testid="team-absences-grid">
      <CardHeader className="pb-2">{toolbar}</CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
          </div>
        ) : filteredEmployees.length === 0 ? (
          <p className="text-center text-slate-500 py-8">
            {employees.length === 0 ? 'Aucun collaborateur' : 'Aucun collaborateur ne correspond à la recherche'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="border-collapse text-xs w-full">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2 text-left min-w-[170px] font-medium">
                    Collaborateur
                  </th>
                  {monthDays.map((d) => {
                    const jf = getJourFerie(d.dateStr);
                    const solidarite = isJourneeSolidarite(d.dateStr);
                    return (
                      <th
                        key={d.dateStr}
                        className={`border border-slate-200 dark:border-slate-700 p-1 text-center min-w-[26px] font-normal ${
                          jf ? 'bg-red-50 dark:bg-red-900/20 text-red-600'
                            : solidarite ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600'
                            : d.isWeekend ? 'bg-slate-100 dark:bg-slate-800 text-slate-400'
                            : d.dateStr === todayStr ? 'bg-[#0F5156]/10 text-[#0F5156] font-semibold'
                            : 'text-slate-500'
                        }`}
                        title={jf ? jf.nom : solidarite ? 'Journée de solidarité (travaillée)' : d.date.toLocaleDateString('fr-FR', { weekday: 'long' })}
                      >
                        <div>{WEEKDAY_LETTERS[d.dow]}</div>
                        <div className="font-medium">{d.day}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((emp) => (
                  <tr key={emp.id} className={emp.inactif ? 'opacity-70' : ''}>
                    <td className={`sticky left-0 z-10 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2 font-medium whitespace-nowrap ${emp.inactif ? 'text-slate-400 italic' : ''}`}>
                      {emp.name}
                      {emp.inactif && (
                        <span className="ml-1 text-[10px] font-normal text-slate-400 not-italic" title="Collaborateur inactif ou sorti">(sorti)</span>
                      )}
                      {emp.department && <span className="block text-[10px] font-normal text-slate-400">{emp.department}</span>}
                    </td>
                    {monthDays.map((d) => renderCell(emp, d))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-slate-400 mt-2">
              Case colorée = absence (opacité réduite = en attente de validation, ½ = demi-journée, plusieurs bandes = absences superposées).
              Colonnes grisées = week-ends et jours fériés.
            </p>
          </div>
        )}

        {showLegend && !loading && (
          <div className="flex flex-wrap gap-3 mt-4 px-1">
            <div className="flex items-center gap-1.5">
              <div className="w-3.5 h-3.5 rounded bg-red-500 flex items-center justify-center">
                <Star className="w-2.5 h-2.5 text-white fill-white" />
              </div>
              <span className="text-xs text-slate-500">Jour férié</span>
            </div>
            {journeeSolidarite?.travaillee && (
              <div className="flex items-center gap-1.5">
                <div className="w-3.5 h-3.5 rounded bg-purple-500" />
                <span className="text-xs text-slate-500">Journée de solidarité (travaillée)</span>
              </div>
            )}
            {Object.entries(typeColors).map(([type, color]) => (
              <div key={type} className="flex items-center gap-1.5">
                <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: color }} />
                <span className="text-xs text-slate-500">{typeLabel(type)}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5">
              <div className="w-3.5 h-3.5 rounded bg-slate-400 opacity-50" />
              <span className="text-xs text-slate-500">En attente de validation</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamAbsencesGrid;
